/**
 * The `combo` visual adapter (SPEC §9.4, §9.2 "combo axes").
 *
 * The one adapter where each series carries its own `kind` (line / bar / area)
 * and may sit on a right-hand axis — both of which the manifest validator
 * refuses on any other visual (`COMBO_ONLY_VISUAL`). `audit_volume` is the
 * report that needs it: events as bars on the left axis, the running total as a
 * line on the right, where a single shared axis would flatten one of them.
 *
 * A right axis appears only when a series asks for one, and each axis takes its
 * number formatting from the first series that lands on it, so the left ticks
 * read in the units of the left series and the right ticks in the right's.
 */

import type { BarSeriesOption, EChartsOption, LineSeriesOption } from 'echarts'

import {
  axisLabel,
  axisLabelFormatter,
  axisLine,
  axisTooltipFormatter,
  baseOption,
  baseTooltip,
  seriesColor,
  splitLine,
} from '../../lib/charts'
import type { Cell, ColumnSpec, SeriesSpec, VisualSpec } from '../../lib/types'
import { categoryData, resolveSeriesColumns, resolveXColumn, seriesData } from './cartesian'

type ComboSeries = BarSeriesOption | LineSeriesOption

/** One combo series. A null `kind` defaults to a line — the neutral reading. */
function comboSeries(
  series: SeriesSpec,
  data: (number | null)[],
  name: string,
  color: string,
): ComboSeries {
  const yAxisIndex = series.axis === 'right' ? 1 : 0
  if (series.kind === 'bar') {
    return { type: 'bar', name, data, yAxisIndex, barMaxWidth: 40, itemStyle: { color } }
  }
  return {
    type: 'line',
    name,
    data,
    yAxisIndex,
    itemStyle: { color },
    lineStyle: { width: 2 },
    symbol: 'circle',
    symbolSize: 5,
    smooth: false,
    // 'area' is a filled line; a null kind is a plain one.
    ...(series.kind === 'area' ? { areaStyle: {} } : {}),
  }
}

export function comboOption(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
  accent: string,
): EChartsOption {
  const xColumn = resolveXColumn(visual, columns)
  const categories = categoryData(visual, columns, rows)
  const seriesColumns = resolveSeriesColumns(visual, columns)

  if (seriesColumns.length === 0) {
    throw new Error(`visual ${visual.id} is a combo with no series`)
  }

  // Each axis is labelled in the units of the first series that sits on it, so
  // a right axis carrying a percentage does not borrow the left's integer ticks.
  const firstOnAxis = (side: 'left' | 'right'): ColumnSpec | undefined =>
    seriesColumns.find((_column, index) => (visual.series[index]?.axis ?? 'left') === side)
  const leftColumn = firstOnAxis('left') ?? seriesColumns[0]
  const rightColumn = firstOnAxis('right')

  const valueAxis = (column: ColumnSpec | undefined) => ({
    type: 'value' as const,
    axisLabel: { ...axisLabel(), ...(column ? { formatter: axisLabelFormatter(column) } : {}) },
    splitLine: splitLine(),
  })

  return {
    ...baseOption(),
    tooltip: {
      ...baseTooltip(),
      // Mixed marks, so the pointer is a shadow band spanning the category
      // rather than a crosshair that only tracks the line.
      axisPointer: { type: 'shadow' },
      formatter: axisTooltipFormatter(xColumn, seriesColumns),
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { ...axisLabel(), formatter: axisLabelFormatter(xColumn) },
      axisLine: axisLine(),
      axisTick: { show: false },
    },
    // A second axis only when a series asks for the right; `splitLine` off on
    // it so two grids do not overprint each other.
    yAxis: rightColumn
      ? [valueAxis(leftColumn), { ...valueAxis(rightColumn), splitLine: { show: false } }]
      : valueAxis(leftColumn),
    series: visual.series.map((series, index) =>
      comboSeries(
        series,
        seriesData(columns, rows, series.column),
        seriesColumns[index]?.label ?? series.column,
        seriesColor(series, accent, index),
      ),
    ),
    ...(rows.length > 60
      ? { dataZoom: [{ type: 'inside' as const }, { type: 'slider' as const, height: 18 }] }
      : {}),
  }
}
